/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { createStyles } from "@material-ui/core/styles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";

import {
  Popover,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
} from "@material-ui/core";
import { Tooltip, TextField, IconButton } from "@material-ui/core";
import {
  DeleteForeverRounded,
  TimerOff,
  Update,
  PlayCircleOutline,
} from "@material-ui/icons";

import {
  sDelNote,
  sUpdateVB,
  sClearVB,
  sNoteLabel,
  sNoteText,
  sProceed,
  sCancel,
  sCannotUndo,
  sImSure,
  sDiscard,
} from "assets/jss/views/ResStrings";
import { EntryActionType } from "./NotesZone";
import { VideoPlayer } from "./VideoPlayer";
import { Modal } from "components/Modal/Modal";

import { showToast, ApricotAction } from "client/actions/modal";

// A custom hook produces http access for NoteEntry
import useHttp from "client/hooks/httpFetch";

import notesZoneStyle from "assets/jss/views/classroomPage/notesZoneStyle";
const styles = createStyles(notesZoneStyle as any);

import { theme } from "client/../helpers/mui";
import {
  idSaveNote,
  idNtBody,
  idOpenNt,
  idDelNote,
  idNtPlayV,
  idNtSetupV,
  idNtClearV,
  idNtClrPicker,
  idNtColor,
} from "assets/jss/views/ElementIDs";
import { PlayerState, playerActivated } from "./PlayerZone";

export type NoteEntryType = {
  label?: string | null;
  text?: string | null;
  clr?: string;
  vpos: number;
  seq: number;
  expanded?: boolean;
  _id?: string;
};

type NoteFilter = {
  user: string;
  course: string;
  lecture: string;
};

interface Props extends WithStyles<typeof styles> {
  showToast: ApricotAction;
  playerState: PlayerState;
  expandPane?: boolean | undefined;
  filter: NoteFilter;
  note: NoteEntryType;
  newEntry?: boolean | false;
  player: VideoPlayer;
  entryAction: (params: EntryActionType) => void;
  portrait: boolean;
  classes: any;
}

const NoteEntryBase: React.FC<Props> = ({
  showToast,
  playerState,
  classes,
  expandPane,
  filter,
  note,
  newEntry,
  player,
  entryAction,
  portrait,
}: Props) => {
  const bodyTxtLimit = 800;
  const labelTxtLimit = 80;
  const [bodyTxtCount, setBodyTxtCount] = useState(
    note && note.text ? note.text.length : 0,
  );
  const [showTxtCount, setShowTxtCount] = useState(newEntry);

  const { user, course, lecture } = filter;

  // We use ref, instead of state, to keep track the editing content and the last-saved content, since
  // ref can be seen during unmount, while state always present their initial state in unmount

  // currently editing note
  const refLabel = useRef(note.label);
  const refText = useRef(note.text);
  const refColor = useRef(note.clr);
  const refVPos = useRef(note.vpos);
  const refId = useRef(note._id);
  const refExpand = useRef(note.expanded || false);

  // changing ref won't cause render, so use a render toggle to trigger redraw
  const [renderToggle, setRenderToggle] = useState(false);

  // for color picker popover
  const [anchorEl, setAnchorEl] = useState<any>(null);

  // for showing delete warning
  const [showAlert, setShowAlert] = useState(false);
  const [alertModal, setAlertModal] = useState(<></>);

  // copy of last saved note
  const refSavedNote = useRef(note);

  // http actions
  const UPDATE = "UPDATE_NOTE";
  const DELETE = "DELETE_NOTE";
  const ADD = "ADD_NOTE";

  const notesColor = theme.palette.notes.main;
  const notesBackground = theme.palette.notes.background;

  const {
    httpSending,
    httpError,
    httpData,
    sendReq,
    reqType,
    // clear,
  } = useHttp();

  const currentNote = () => {
    return {
      label: refLabel.current,
      text: refText.current,
      clr: refColor.current,
      vpos: refVPos.current,
      seq: note.seq,
      _id: refId.current,
    };
  };

  const syncStore = (rotate: boolean) => {
    if (refId.current) {
      const curNote = currentNote();
      // send only the change since mounted to Store
      const updateSinceMounted = getUpdateParts(curNote, note);
      const updated = Object.keys(updateSinceMounted).length !== 0;
      if (!updated) return;

      // merge this entry into the redux Store
      if (newEntry) {
        // If it's a new entry, send everything back; because it may not be in Store at all.
        entryAction({
          action: "nz_update",
          data: { lecture, noteId: refId.current, note: curNote },
          // NotesZone may get re-rederned in newEntry mode, so we keep the new note for that case.
          // However, since we clear newEntry when rotate (in NotesZone), don't keep the new note.
          newEntry: rotate ? false : true,
        });
      } else {
        if (Object.keys(updateSinceMounted).length) {
          entryAction({
            action: "nz_update",
            data: {
              lecture,
              noteId: refId.current,
              note: updateSinceMounted,
            },
          });
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      // When unmount, if we already have a noteId, then save whatever unsaved
      if (refId.current) {
        const curNote = currentNote();

        // save the difference since last save
        const updateSinceSaved = getUpdateParts(curNote, refSavedNote.current);
        if (Object.keys(updateSinceSaved).length) {
          writeNoteHandler(updateSinceSaved, false);
        }

        syncStore(false); // false: not caused by rotation
      }
    };
  }, []);

  // store the persistent data in refSavedNote
  useEffect(() => {
    if (!httpSending && !httpError) {
      switch (reqType) {
        case UPDATE:
          refSavedNote.current = { ...refSavedNote.current, ...httpData };
          break;
        case ADD:
          refId.current = httpData._id;
          refSavedNote.current = httpData;
          setRenderToggle(!renderToggle);
          break;
        case DELETE:
          entryAction({
            action: "nz_delete",
            noteId: httpData.noteId,
            newEntry,
          });
          break;
        default:
          break;
      }
    }
  }, [httpData, reqType, httpSending, httpError]);

  useEffect(() => {
    // note in Store may be changed (e.g., device in different orientation)
    // In that case, we feed the current with the Store value.
    refLabel.current = note.label;
    refText.current = note.text;
    refColor.current = note.clr;
    refVPos.current = note.vpos;
    refExpand.current = note.expanded || false;
    setRenderToggle(!renderToggle);
  }, [note]);

  useEffect(() => {
    // clear alert, if any
    setShowAlert(false);

    // Update store
    syncStore(true);
  }, [portrait]);

  const getUpdateParts = useCallback((noteNew, noteOrg) => {
    let updateParts = {};
    Object.keys(noteNew).forEach((field) => {
      if (noteNew[field] !== noteOrg[field]) {
        updateParts = { ...updateParts, [field]: noteNew[field] };
      }
    });
    return updateParts;
  }, []);

  const writeNoteHandler = useCallback(
    (update, dispatch = true) => {
      if (refId.current) {
        // Update note
        const reqData = {
          filter: { course, noteId: refId.current },
          update,
        };
        sendReq(
          `/api/nt/${user}`,
          "PUT",
          JSON.stringify(reqData),
          UPDATE,
          dispatch,
        );
      } else {
        // Add new note
        const noteInfo = { course, lecture, ...currentNote() };
        sendReq(
          `/api/nt/${user}`,
          "POST",
          JSON.stringify(noteInfo),
          ADD,
          dispatch,
        );
      }
    },
    [sendReq, user, course, lecture, currentNote],
  );

  const deleteNoteHandler = () => {
    if (refId.current) {
      setAlertModal(
        <Modal
          color="warning"
          title={sDelNote}
          buttons={[sProceed, sCancel]}
          onButtons={(id: number) => {
            if (id === 0) execDeleteNote();
          }}
          onExit={() => setShowAlert(false)}
          labelChk={sImSure}>
          {sCannotUndo}
        </Modal>,
      );
      setShowAlert(true);
    } else {
      entryAction({ action: "nz_endNewEntry" });
    }
  };

  const execDeleteNote = useCallback(() => {
    // Note: since we checked refId.current in deleteNoteHandler, refId.current is not null here
    const reqData = { course, noteId: refId.current };
    sendReq(`/api/ntd/${user}`, "PUT", JSON.stringify(reqData), DELETE);

    // To prevent the unmount code writing anything, clear the noteId
    refId.current = undefined;
  }, [sendReq, user, course, refId.current]);

  // Note editing handler
  useEffect(() => {
    const timer = setTimeout(
      () => {
        const updateParts = getUpdateParts(currentNote(), refSavedNote.current);
        if (Object.keys(updateParts).length) {
          writeNoteHandler(updateParts);
        }
      },
      refId.current ? 1000 : 200,
    );
    return () => {
      clearTimeout(timer);
    };
  }, [refLabel.current, refText.current, refColor.current, refVPos.current]);

  const labelEdit = () => (
    <TextField
      classes={{ root: classes.labelRoot }}
      InputProps={{ disableUnderline: true }}
      inputProps={{ className: classes.labelAttr }}
      autoFocus={newEntry}
      fullWidth={true}
      placeholder={sNoteLabel}
      margin="normal"
      value={refLabel.current || ""}
      onClick={(e: any) => e.stopPropagation()}
      onChange={(e: any) => {
        refLabel.current = e.target.value.substr(0, labelTxtLimit);
        setRenderToggle(!renderToggle);
      }}
    />
  );

  const bodyEdit = () => (
    <TextField
      id={`${idNtBody}${note.seq}`}
      InputProps={{ disableUnderline: true }}
      inputProps={{ className: classes.textAttr }}
      classes={{ root: classes.inputRoot }}
      multiline={true}
      fullWidth={true}
      placeholder={sNoteText}
      margin="normal"
      value={refText.current || ""}
      onClick={(e: any) => e.stopPropagation()}
      onChange={(e: any) => {
        showTxtCount || setShowTxtCount(true);
        const text = e.target.value.substr(0, bodyTxtLimit);
        refText.current = text;
        setBodyTxtCount(text.length);
        setRenderToggle(!renderToggle);
      }}
    />
  );

  const expansionButton = (tips: string, expand: boolean) => {
    const btnStyle = {
      minWidth: "1.8rem",
      minHeight: "1.8rem",
      backgroundColor: "transparent",
      cursor: "pointer",
    };
    return (
      <Tooltip title={tips} arrow>
        <div
          id={`${idOpenNt}${note.seq}`}
          onClick={(e: any) => {
            e.stopPropagation();
            onPanelChange(expand);
          }}>
          <span style={{ color: refColor.current || notesColor }}>
            <i style={btnStyle} className="far fa-sticky-note" />
          </span>
        </div>
      </Tooltip>
    );
  };

  const toggleColorPal = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    open: Boolean,
  ) => {
    event.preventDefault();
    setAnchorEl(open ? event.currentTarget : null);
  };

  const colorButton = (color: string, idx: number) => (
    <Tooltip title={`Change label color`} key={color} arrow>
      <div
        style={{
          color,
          display: "inline-block",
          margin: "6px",
          cursor: "pointer",
        }}
        id={`${idNtColor}${idx}`}
        onClick={(e: any) => {
          toggleColorPal(e, false);
          refColor.current = color;
          setRenderToggle(!renderToggle);
        }}>
        <i
          style={{ minWidth: "24px", minHeight: "24px" }}
          className="far fa-sticky-note"
        />
      </div>
    </Tooltip>
  );

  const colorPicker = () => {
    const openColor = Boolean(anchorEl);
    const id = openColor ? "notes_colors-popover" : undefined;
    const colors = {
      red: "#C61D39",
      orange: "#FFC300",
      green: "#39C61D",
      blue: "#1D39C6",
      brown: "#C6561D",
      pink: "#FF69B4",
      purple: "#911DC6",
      notes: notesColor,
    };

    return (
      <Popover
        id={id}
        open={openColor}
        anchorEl={anchorEl}
        onClose={(e: any) => toggleColorPal(e, false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <div style={{ margin: "4px" }}>
          {Object.keys(colors).map((color: string, idx: number) =>
            colorButton(colors[color], idx),
          )}
        </div>
      </Popover>
    );
  };

  const vidoeTime = () => {
    const min = Math.floor(refVPos.current / 60);
    const sec = Math.floor(refVPos.current % 60);
    const secStr = sec.toString();
    return `${min}:${sec < 10 ? `0${secStr}` : secStr}`;
  };

  const videoButton = (
    tips: string,
    disabled: boolean,
    action: () => void,
    icon: React.ReactNode,
    id: string,
  ) => {
    const btn = (
      <IconButton
        id={`${id}${note.seq}`}
        color="primary"
        size="small"
        disabled={disabled}
        classes={{ colorPrimary: classes.vbButton }}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            action();
            if (tips === sClearVB || tips === sUpdateVB)
              setRenderToggle(!renderToggle);
          }
        }}>
        {icon}
      </IconButton>
    );
    return disabled ? (
      btn
    ) : (
      <Tooltip title={tips} arrow>
        {btn}
      </Tooltip>
    );
  };

  const playButton = () => {
    const playable = refVPos.current && refVPos.current !== -1;
    return videoButton(
      `Play from ${vidoeTime()}`,
      !playable,
      () => entryAction({ action: "play", vPos: refVPos.current }),
      <PlayCircleOutline />,
      idNtPlayV,
    );
  };

  const noteLabelArea = () => {
    return refExpand.current === true ? (
      // show label in expanded state
      <div style={{ display: "flex", alignItems: "center", width: "98%" }}>
        {expansionButton("Close note", false)}
        {playButton()}
        {videoButton(
          sUpdateVB,
          !playerActivated(playerState),
          () => {
            refVPos.current = player ? Math.floor(player.getPosition()) : -1;
            showToast({ type: "normal", message: "Video bookmark is set" });
          },
          <Update />,
          idNtSetupV,
        )}
        {videoButton(
          sClearVB,
          refVPos.current === -1,
          () => {
            refVPos.current = -1; // disable if no vPos is marked
            showToast({ type: "normal", message: "Video bookmark cleared" });
          },
          <TimerOff />,
          idNtClearV,
        )}
        {labelEdit()}
      </div>
    ) : (
      // show label in non-expanded state
      <div style={{ display: "flex", alignItems: "center", width: "89%" }}>
        {expansionButton("Open note", true)}
        {playButton()}
        <Tooltip title="Expand to view or edit" arrow>
          <div style={{ fontFamily: "Open Sans" }}>
            {refLabel.current || (
              <span style={{ color: "#A0A0A0" }}>
                Click to enter the note/bookmark.
              </span>
            )}
          </div>
        </Tooltip>
      </div>
    );
  };

  const noteBodyArea = () => {
    const txtCount = showTxtCount && (
      <p style={{ fontSize: "0.75rem", margin: "0 auto" }}>
        {bodyTxtLimit - bodyTxtCount}
      </p>
    );

    return (
      <div className={classes.noteBody}>
        <Tooltip title="Set color of note label" arrow>
          <IconButton
            id={`${idNtClrPicker}${note.seq}`}
            onClick={(e: any) => toggleColorPal(e, true)}>
            <img
              src={require("assets/svg/colorPal.svg")}
              className={classes.colorPal}
            />
          </IconButton>
        </Tooltip>
        {colorPicker()}
        <div className={classes.noteBodyEdit}>{bodyEdit()}</div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {txtCount}
          <Tooltip title={newEntry ? sDiscard : sDelNote} arrow>
            <IconButton
              id={`${idDelNote}${note.seq}`}
              color="primary"
              size="small"
              classes={{ colorPrimary: classes.notesColor }}
              onClick={deleteNoteHandler}>
              <DeleteForeverRounded />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  const onPanelChange = (expanded: boolean) => {
    refExpand.current = expanded;
    setRenderToggle(!renderToggle);

    if (!newEntry) {
      entryAction({ action: "nz_expanded", noteId: refId.current, expanded });
    } else {
      if (!expanded) {
        // End newEntry mode if user collapse the entry. This is the only way to end newEntry mode manually.
        entryAction({ action: "nz_endNewEntry" });
      }
    }
  };

  useEffect(() => {
    if (expandPane !== undefined) {
      refExpand.current = expandPane;
      setRenderToggle(!renderToggle);
    }
  }, [expandPane]);

  const expandIcon = () =>
    newEntry && (
      <Tooltip title="Done" arrow>
        <img
          id={idSaveNote}
          src={require(`assets/svg/save${refId.current ? "R" : "G"}.svg`)}
          className={classes.doneButton}
        />
      </Tooltip>
    );

  return (
    <>
      <div style={{ width: "100%" }}>
        <ExpansionPanel
          expanded={refExpand.current}
          onChange={(event: object, expanded: boolean) =>
            onPanelChange(expanded)
          }>
          <ExpansionPanelSummary
            style={{ backgroundColor: notesBackground }}
            classes={{
              root: classes.noteLabel,
              expanded: classes.noteLabelExp,
            }}
            IconButtonProps={{
              color: "primary",
              disabled: !refId.current,
              classes: { colorPrimary: classes.notesColor },
            }}
            expandIcon={expandIcon()}>
            {/* aria-controls="panel1c-content" id="panel1c-header"> */}
            {noteLabelArea()}
          </ExpansionPanelSummary>
          <ExpansionPanelDetails classes={{ root: classes.noteBodyPanel }}>
            {noteBodyArea()}
          </ExpansionPanelDetails>
        </ExpansionPanel>
      </div>

      {showAlert && alertModal}
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    playerState: state.state_modal.playerState,
  };
}

const base: any = NoteEntryBase;
export const NoteEntry = withStyles(styles)(
  connect(mapStateToProps, {
    showToast,
  })(base),
);
